import React, { memo, useCallback, useReducer, useState } from 'react';
import { View, FlatList } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import {
  Space,
  LoadingContainer,
  WebScrollView,
} from '@redlink/components/atoms';
import { PreviewContainer } from '@redlink/components/molecules';
import {
  OrderStatusList,
  OrderItem,
  Pagination,
} from '@redlink/components/muscles';
import { OrderDetailPage } from '@redlink/components/pages';
import { ListContainer, OrderItemDetail } from '@redlink/components/organisms';
import { useLocale } from '@redlink/assets/locale';
import {
  cache,
  useQuery,
  useMutation,
  getPageOrdersQuery,
  setOrderStatusQuery,
  setOrderItemsStatusQuery,
} from '@redlink/services';
import { useValues } from '@redlink/assets/values';
import { initialState, reducer, types } from './store';
import {
  freshCacheForChangeStatus,
  freshCacheForChangeItemsStatus,
} from './cache';

const PAGE_SIZE = 10;

export const OrderPage = memo(({ navigation }) => {
  const { styles } = useStyle();
  const { dictionary } = useLocale();
  const { staticData } = useValues();
  const [orderDetailIsVisible, setOrderDetailIsVisible] = useState(false);

  const [
    { selectedStatus, fetchMoreIsBusy, currentPageIndex, selectedOrderIndex },
    dispatch,
  ] = useReducer(reducer, initialState);

  const renderItem = useCallback(
    ({ item, index }) => {
      return (
        <OrderItem
          {...{ item }}
          isSelected={index === selectedOrderIndex}
          onPress={() => {
            dispatch({
              type: types.SET_SELECTED_ORDER_INDEX,
              payload: index,
            });
            setOrderDetailIsVisible(true);
          }}
        />
      );
    },
    [selectedOrderIndex]
  );

  const {
    fetchMore,
    loading: getPageOrdersIsBusy,
    data: { Orders: { totalCount = 0, items: Orders = [] } = {} },
  } = useQuery(getPageOrdersQuery, {
    variables: {
      skip: 0,
      take: PAGE_SIZE,
    },
  });

  const fetchMoreOrders = (pageIndex, status) => {
    dispatch({
      type: types.SET_FETCH_MORE_IS_BUSY,
      payload: true,
    });
    fetchMore({
      variables: {
        ...(status && { status }),
        skip: pageIndex * PAGE_SIZE,
        take: PAGE_SIZE,
      },
      updateQuery: (prev, { fetchMoreResult, variables }) => {
        dispatch({
          type: types.SET_FETCH_MORE_IS_BUSY,
          payload: false,
        });
        if (!fetchMoreResult) return prev;

        const newResult = {
          ...prev,
          Orders: {
            ...prev.Orders,
            totalCount: prev.Orders.totalCount,
            items: [...fetchMoreResult.Orders.items],
          },
        };

        cache.writeQuery({
          query: getPageOrdersQuery,
          data: newResult,
          variables,
        });

        return newResult;
      },
    });
  };

  const [setOrderStatus, { loading: setOrderStatusIsBusy }] = useMutation(
    setOrderStatusQuery,
    {
      done: () => {},
      update: freshCacheForChangeStatus({
        skip: currentPageIndex * PAGE_SIZE,
        take: PAGE_SIZE,
      }),
    }
  );

  const [
    setOrderItemsStatus,
    { loading: setOrderItemsStatusIsBusy },
  ] = useMutation(setOrderItemsStatusQuery, {
    done: () => {},
    update: freshCacheForChangeItemsStatus({
      skip: currentPageIndex * PAGE_SIZE,
      take: PAGE_SIZE,
    }),
  });

  const onChangeStatus = useCallback(
    (id, status) => setOrderStatus({ id, status }),
    []
  );

  const onChangeItemStatus = useCallback(
    (id, items, value) =>
      setOrderItemsStatus({
        orderId: id,
        status: value
          ? staticData.orderItemStatus.done
          : staticData.orderItemStatus.none,
        orderItemsIds: items,
      }),
    []
  );

  return (
    <View style={styles.container}>
      <WebScrollView>
        <ListContainer
          title={dictionary.status}
          containerContainerStyle={styles.categoriesContainerStyle}
        >
          <OrderStatusList
            selectedId={selectedStatus}
            contentContainerStyle={styles.contentContainerStyle}
            onChange={(item, index) => {
              dispatch({
                type: types.SET_MULTI,
                payload: {
                  currentPageIndex: 0,
                  selectedStatus: item,
                  selectedOrderIndex: 0,
                },
              });
              fetchMoreOrders(0, item);
            }}
          />
        </ListContainer>
        <Space size={16} />
        <ListContainer
          data={Orders}
          title={dictionary.orders}
          isFetching={getPageOrdersIsBusy}
          placeholder={{
            description: dictionary.noOrdersExist,
          }}
        >
          <LoadingContainer isBusy={fetchMoreIsBusy} hasOverlay={false}>
            <FlatList
              contentContainerStyle={styles.contentContainerStyle}
              data={Orders}
              ItemSeparatorComponent={() => <Space />}
              {...{ renderItem }}
              showsVerticalScrollIndicator={false}
            />
          </LoadingContainer>
        </ListContainer>
        <Space size={24} />
        {totalCount > PAGE_SIZE && (
          <Pagination
            style={styles.paginationContainer}
            pageSize={PAGE_SIZE}
            totalCount={totalCount}
            currentPage={currentPageIndex}
            onChange={(pageIndex) => {
              dispatch({
                type: types.SET_CURRENT_PAGE_INDEX,
                payload: pageIndex,
              });
              fetchMoreOrders(pageIndex, selectedStatus);
            }}
          />
        )}
      </WebScrollView>
      <PreviewContainer
        title={dictionary.orderDetail}
        mobileContent={
          <OrderDetailPage
            isBusy={setOrderStatusIsBusy || setOrderItemsStatusIsBusy}
            item={Orders[selectedOrderIndex]}
            {...{ onChangeStatus, onChangeItemStatus }}
            isVisible={orderDetailIsVisible}
            onDismiss={() => setOrderDetailIsVisible(false)}
          />
        }
      >
        {Orders[selectedOrderIndex] && (
          <OrderItemDetail
            isBusy={setOrderStatusIsBusy || setOrderItemsStatusIsBusy}
            item={Orders[selectedOrderIndex]}
            {...{ onChangeStatus, onChangeItemStatus }}
          />
        )}
      </PreviewContainer>
    </View>
  );
});

const useStyle = createUseStyle(({ colors, size }) => ({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  contentContainerStyle: {
    paddingVertical: 8,
  },
  categoriesContainerStyle: {
    marginTop: 16,
  },
}));
